import React from 'react';

const ProjectsHelp: React.FC = () => {
  return (
    <>
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Projects
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The Projects page allows you to manage and track all your organization's
          projects. View project status, timelines, budgets, team assignments,
          and deliverables in one central location.
        </p>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Project Overview
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Each project displays key metrics at a glance:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Status:</strong> Active, On Hold, Completed, or Cancelled</li>
          <li><strong>Timeline:</strong> Start and end dates with duration</li>
          <li><strong>Budget:</strong> Allocated amount vs. spent with progress bar</li>
          <li><strong>Milestone Progress:</strong> Percentage of completed milestones</li>
          <li><strong>Client:</strong> Associated client organization</li>
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Delivery Pod
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          The Delivery Pod is the team assigned to work on a project:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Team Members:</strong> People assigned to the project</li>
          <li><strong>Roles:</strong> Each member's role (e.g., Tech Lead, Developer)</li>
          <li><strong>Hourly Rate:</strong> Billing rate for time tracking</li>
          <li><strong>Add/Remove:</strong> Partners can manage team composition</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Team members in the Delivery Pod can log time against the project.
        </p>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Project Milestones
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Milestones track major deliverables and project phases:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Name & Description:</strong> What the milestone represents</li>
          <li><strong>Status:</strong> Not Started, In Progress, or Completed</li>
          <li><strong>Dates:</strong> Planned start and end dates</li>
          <li><strong>Jira Issues:</strong> Linked issues from Jira integration</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Click "+ Add Milestone" to create new milestones. Use the Sync button
          to pull latest Jira issue status.
        </p>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Statement of Work (SoW)
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Manage project documentation and contracts:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Upload PDF:</strong> Upload SoW documents directly (max 10MB)</li>
          <li><strong>Google Drive:</strong> Link to a Google Drive folder for documents</li>
          <li><strong>Download:</strong> Access uploaded documents anytime</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Only partners can upload or modify SoW documents.
        </p>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Time Tracking
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Track hours worked by team members:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Log Hours:</strong> Record time spent on project tasks</li>
          <li><strong>Description:</strong> What work was performed</li>
          <li><strong>Date & Duration:</strong> When and how long</li>
          <li><strong>Billing Summary:</strong> Total hours and calculated costs</li>
          <li><strong>Filters:</strong> Filter by date range or team member</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Time entries help track budget utilization and generate billing reports.
        </p>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Funding Requests
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          View AWS credits and funding linked to the project:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Requested Amount:</strong> Total funding requested</li>
          <li><strong>Approved Amount:</strong> Amount approved by AWS</li>
          <li><strong>Status:</strong> Draft, Submitted, Approved, or Rejected</li>
          <li><strong>Program:</strong> Type of funding program (Activate, MDF, etc.)</li>
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Tagging &amp; Cost Tracking
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          The Tagging section lets you configure how infrastructure costs are
          attributed to this project. There are two approaches:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Project Tags:</strong> Define tag key/value pairs directly on the project (e.g., Project=my-app). These match against AWS resource tags.</li>
          <li><strong>Linked Workloads:</strong> Link existing workloads that already have tag pairs configured. The project inherits cost tracking from its linked workloads.</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Use the <strong>Enable Cost Tracking</strong> toggle to activate cost
          allocation. The tag key must be activated as a Cost Allocation Tag in
          your AWS Billing console for costs to appear in FinOps breakdowns.
        </p>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          AWS Environments
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          AWS environments associated with the project:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li>Link AWS accounts and environments to scope the project</li>
          <li>View linked environments and their types</li>
          <li>Unlink environments that are no longer relevant</li>
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Project &amp; Workload Assignment
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Across the platform, you can manually assign projects and workloads to resources:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Bulk assignment:</strong> Select items in any list and use the Project/Workload dropdowns in the selection bar</li>
          <li><strong>Individual assignment:</strong> Use the dropdowns on detail pages to set project or workload per item</li>
          <li><strong>Auto-assignment:</strong> Resources matching project or workload tags are automatically assigned by Aivilo Lens</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Project badges appear in green; workload badges appear in purple across all list views.
        </p>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Project Actions
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Available actions for managing projects:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Edit Name:</strong> Click the pencil icon to rename</li>
          <li><strong>Change Status:</strong> Update project status</li>
          <li><strong>Sync Jira:</strong> Pull latest data from Jira</li>
          <li><strong>Export:</strong> Download project data</li>
          <li><strong>Delete:</strong> Remove project (partners only)</li>
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Creating Projects
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Partners and administrators can create new projects by clicking
          "New Project". Fill in project details including name, client,
          dates, budget, and optionally link to a Jira project for
          milestone synchronization.
        </p>
      </div>

    </>
  );
};

export default ProjectsHelp;
